import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { defer, Observable } from 'rxjs';
import { RequestApiConnection } from './request-api-connection';

// angular-oauth2-oidc joins `<nonce>;<additionalState>` into the OAuth2
// `state` parameter with this separator (library default).
const NONCE_STATE_SEPARATOR = ';';

/**
 * sessionStorage flag set when an authentication failure invalidated the
 * current token. While set, the next {@link RequestApiOAuthService.login}
 * forces a fresh provider login prompt (`prompt=login`) instead of letting the
 * provider silently re-issue a token from a still-valid SSO session. Lives in
 * sessionStorage so it survives both a manual reload and the full-page
 * end-session redirect round-trip.
 */
const FORCE_LOGIN_STORAGE_KEY = 'request-api:oauth:force-login';

/**
 * Access tokens are refreshed once their remaining lifetime drops below this
 * margin instead of waiting for them to actually expire. Without it a token
 * with a second of life left is attached to a request and arrives at the
 * server already expired — the network round-trip plus any client/server
 * clock skew is enough to push it past `exp` — which is exactly the
 * `ERR_JWT_EXPIRED` verification failure the server logs.
 */
const ACCESS_TOKEN_EXPIRY_MARGIN_MS = 60_000;

/**
 * Ensures the OIDC `offline_access` scope is requested so the provider issues
 * a refresh token. Without it the authorization-code flow yields only an
 * access token and there is nothing to renew with, forcing a full re-login the
 * moment the token expires. Left untouched for manual (non-OIDC) endpoints,
 * where refresh-token issuance is governed by the server rather than a
 * standard scope and an unknown scope may be rejected outright.
 */
function withOfflineAccess(scope: string): string {
  const scopes = scope.split(/\s+/).filter(Boolean);
  if (!scopes.includes('offline_access')) {
    scopes.push('offline_access');
  }
  return scopes.join(' ');
}

/** Fixed redirect URI for the request api OAuth2 code flow — must be registered with the identity provider. */
export function requestApiOAuthRedirectUri(): string {
  return `${location.origin}/upload/request-api`;
}

/**
 * Thrown when the authorization-code token exchange (a cross-origin POST to
 * the identity provider's token endpoint) fails with no readable response —
 * status 0 is what a browser reports both for a genuine network outage AND
 * for a CORS-blocked response, and JS cannot tell the two apart. We only
 * throw this from {@link RequestApiOAuthService.handleRedirectCallback},
 * where the authorization step has already round-tripped successfully (we
 * have a `code`), which makes "the token endpoint's CORS config rejected
 * this origin" by far the most likely explanation.
 */
export class RequestApiOAuthCorsError extends Error {
  constructor(public readonly tokenEndpoint: string | null) {
    super(
      `The token request to '${
        tokenEndpoint ?? 'the token endpoint'
      }' failed with no readable response — likely blocked by the identity provider's CORS configuration.`
    );
    this.name = 'RequestApiOAuthCorsError';
  }
}

/**
 * OAuth2 (Authorization Code + PKCE) support for request api connections.
 *
 * Wraps the root `OAuthService` and (re-)configures it per connection. Only
 * exercised for connections with `authMethod === 'oauth2'` — the oauth2-proxy
 * based confidentiality flow and the DID flow never touch it.
 */
@Injectable({ providedIn: 'root' })
export class RequestApiOAuthService {
  private readonly oauthService = inject(OAuthService);

  private configuredFor: string | null = null;

  /**
   * `postLogoutRedirectUri` of the currently configured connection, if any.
   * A full provider-side logout is only attempted when this is set (see
   * {@link invalidateSession}).
   */
  private postLogoutRedirectUri: string | null = null;

  /**
   * Guards against a burst of concurrent triggers — e.g. several 401/403
   * responses in flight together, or one racing a manual disconnect click —
   * each attempting a logout (and, in the provider-logout case, a redundant
   * redirect). Reset at the start of a new sign-in in {@link login}.
   */
  private invalidatingSession = false;

  /**
   * Extracts the connection id from the OAuth2 redirect callback url (it is
   * round-tripped through the `state` parameter by {@link login}). Returns
   * null when the current url is not a code flow callback.
   */
  static parseConnectionIdFromCallback(
    search: string = location.search
  ): string | null {
    const params = new URLSearchParams(search);
    if (!params.has('code') || !params.has('state')) {
      return null;
    }
    const state = params.get('state') ?? '';
    const separatorIndex = state.indexOf(NONCE_STATE_SEPARATOR);
    if (separatorIndex < 0) {
      return null;
    }
    return decodeURIComponent(state.slice(separatorIndex + 1)) || null;
  }

  async configureFor(connection: RequestApiConnection): Promise<void> {
    const oauth2 = connection.oauth2;
    if (!oauth2?.clientId) {
      throw new Error(
        `The request api connection '${connection.name}' has no OAuth2 client configuration.`
      );
    }
    const configKey = `${connection.id}:${connection.updatedAt}`;
    // kept current even on the cached path so handleUnauthorized always sees
    // the active connection's post-logout redirect uri
    this.postLogoutRedirectUri = oauth2.postLogoutRedirectUri ?? null;
    if (this.configuredFor === configKey) {
      return;
    }

    const manualEndpoints =
      !!oauth2.authorizationEndpoint && !!oauth2.tokenEndpoint;
    const issuer = oauth2.issuer;

    const scope = oauth2.scopes ?? 'openid profile';
    const config: AuthConfig = {
      clientId: oauth2.clientId,
      // manual (non-OIDC) endpoints keep the configured scope verbatim; OIDC
      // connections gain `offline_access` so a refresh token is issued
      scope: manualEndpoints ? scope : withOfflineAccess(scope),
      responseType: 'code',
      redirectUri: requestApiOAuthRedirectUri(),
      requireHttps: 'remoteOnly',
      strictDiscoveryDocumentValidation: false,
    };

    if (oauth2.postLogoutRedirectUri) {
      // the library builds the end-session redirect from this + the discovered
      // end_session_endpoint + an id_token_hint; leaving it unset keeps logout
      // local-only (see handleUnauthorized)
      config.postLogoutRedirectUri = oauth2.postLogoutRedirectUri;
    }

    if (manualEndpoints) {
      // plain OAuth2 without OIDC discovery
      config.loginUrl = oauth2.authorizationEndpoint;
      config.tokenEndpoint = oauth2.tokenEndpoint;
      config.oidc = false;
    } else if (issuer) {
      config.issuer = issuer;
    } else {
      throw new Error(
        `The request api connection '${connection.name}' needs either an OAuth2 issuer or authorization + token endpoints.`
      );
    }

    this.oauthService.configure(config);
    if (!manualEndpoints && issuer) {
      await this.loadDiscoveryDocumentWithFallback(issuer, connection.name);
    }
    this.configuredFor = configKey;
  }

  /**
   * Loads the discovery document, trying the OIDC well-known path first and
   * falling back to the plain-OAuth2 (RFC 8414) one — RFC 8414 authorization
   * server metadata is a structural subset of OIDC discovery metadata, so
   * `loadDiscoveryDocument` parses either identically once pointed at it.
   */
  private async loadDiscoveryDocumentWithFallback(
    issuer: string,
    connectionName: string
  ): Promise<void> {
    try {
      await this.oauthService.loadDiscoveryDocument();
      return;
    } catch {
      const fallbackUrl = `${issuer.replace(
        /\/+$/,
        ''
      )}/.well-known/oauth-authorization-server`;
      try {
        await this.oauthService.loadDiscoveryDocument(fallbackUrl);
      } catch {
        throw new Error(
          `Could not discover OAuth2/OIDC endpoints for issuer '${issuer}' of connection '${connectionName}' — tried /.well-known/openid-configuration and /.well-known/oauth-authorization-server.`
        );
      }
    }
  }

  /**
   * Starts the code flow — full page redirect to the authorization server.
   *
   * When a previous request failed authentication (see
   * {@link handleUnauthorized}) the redirect carries `prompt=login`, forcing
   * the provider to re-authenticate the user rather than silently re-issuing a
   * token from a still-valid SSO session. Normal first sign-ins stay seamless.
   */
  async login(connection: RequestApiConnection): Promise<void> {
    await this.configureFor(connection);
    this.invalidatingSession = false;
    const forceLogin = this.consumeForceLogin();
    this.oauthService.initCodeFlow(
      connection.id,
      forceLogin ? { prompt: 'login' } : {},
    );
  }

  /**
   * Completes the code flow after the redirect back from the authorization
   * server. Returns true when a valid access token was obtained.
   */
  async handleRedirectCallback(
    connection: RequestApiConnection
  ): Promise<boolean> {
    await this.configureFor(connection);
    try {
      await this.oauthService.tryLoginCodeFlow();
    } catch (error) {
      // the only network call tryLoginCodeFlow makes is the token exchange
      // POST — a status-0 failure here (no readable response) at this point
      // in the flow is CORS, not a bad connection, by elimination
      if (error instanceof HttpErrorResponse && error.status === 0) {
        throw new RequestApiOAuthCorsError(
          this.oauthService.tokenEndpoint ?? null
        );
      }
      throw error;
    }
    return this.oauthService.hasValidAccessToken();
  }

  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * True when there is a valid access token with more than {@link marginMs} of
   * remaining lifetime. Tokens nearing expiry are treated as stale so they are
   * refreshed before, not after, the server would reject them — see
   * {@link ACCESS_TOKEN_EXPIRY_MARGIN_MS}. Flows that don't expose an
   * expiration fall back to the library's plain validity check.
   */
  private hasFreshAccessToken(marginMs: number): boolean {
    if (!this.oauthService.hasValidAccessToken()) {
      return false;
    }
    const expiresAt = this.oauthService.getAccessTokenExpiration();
    if (!expiresAt) {
      return true;
    }
    return expiresAt - Date.now() > marginMs;
  }

  /**
   * Emits a valid access token or errors. Refreshes proactively once the
   * current token drops below {@link ACCESS_TOKEN_EXPIRY_MARGIN_MS} of
   * remaining lifetime, so an about-to-expire token is never attached to an
   * outgoing request. Never triggers a login redirect — the request api screen
   * reacts to the error and prompts for a re-login.
   */
  getAccessToken$(): Observable<string> {
    return defer(async () => {
      if (this.hasFreshAccessToken(ACCESS_TOKEN_EXPIRY_MARGIN_MS)) {
        return this.oauthService.getAccessToken();
      }
      if (this.oauthService.getRefreshToken()) {
        try {
          await this.oauthService.refreshToken();
        } catch (error) {
          console.warn('OAuth2 token refresh failed', error);
        }
        // a freshly refreshed token clears the margin unless the provider
        // issues tokens shorter-lived than the margin itself, so require only
        // baseline validity here to avoid a needless re-login in that case
        if (this.oauthService.hasValidAccessToken()) {
          return this.oauthService.getAccessToken();
        }
      }
      throw new Error(
        'No valid OAuth2 access token for the request api. Please sign in again.'
      );
    });
  }

  logout(): void {
    this.oauthService.logOut(true);
    this.configuredFor = null;
  }

  /**
   * Deletes the local token so it can never be reused (including after a
   * reload — tokens live in sessionStorage) and forces the user to actually
   * re-authenticate on the next sign-in. Shared by {@link handleUnauthorized}
   * (an authentication failure) and {@link signOut} (a deliberate disconnect).
   *
   * Two mechanisms, applied together:
   * - Always flags the next {@link login} to send `prompt=login`.
   * - When a `postLogoutRedirectUri` is configured *and* the provider exposes
   *   an end-session endpoint (OIDC discovery ran; manual `oidc: false`
   *   endpoints do not), performs a full provider-side logout — a redirect to
   *   the `end_session_endpoint` that destroys the SSO session and returns to
   *   the configured uri. Otherwise the token is cleared locally only.
   *
   * Idempotent within a burst of concurrent triggers via
   * {@link invalidatingSession}, so it results in at most one logout.
   */
  private invalidateSession(): void {
    if (this.invalidatingSession) {
      return;
    }
    this.invalidatingSession = true;
    this.setForceLogin();

    const canProviderLogout =
      !!this.postLogoutRedirectUri && !!this.oauthService.logoutUrl;

    this.configuredFor = null;
    if (canProviderLogout) {
      // full page redirect to the provider's end-session endpoint — clears the
      // local token and destroys the SSO session; execution ends here
      this.oauthService.logOut();
      return;
    }
    // no end-session redirect available: clear the local token only. The
    // prompt=login flag is the fallback that still forces a re-login.
    this.oauthService.logOut(true);
  }

  /** Reacts to an authentication failure (HTTP 401/403) from the request api. */
  handleUnauthorized(): void {
    this.invalidateSession();
  }

  /**
   * Called when the user explicitly disconnects an oauth2 connection. Applies
   * the same hardening as an authentication failure — see
   * {@link invalidateSession} — so a manual disconnect can't be silently
   * undone by reconnecting through a still-valid provider SSO session; the
   * user must actually sign in again.
   */
  signOut(): void {
    this.invalidateSession();
  }

  private setForceLogin(): void {
    try {
      sessionStorage.setItem(FORCE_LOGIN_STORAGE_KEY, '1');
    } catch {
      // sessionStorage unavailable (e.g. privacy mode) — prompt=login is a
      // best-effort hardening, not a correctness requirement
    }
  }

  private consumeForceLogin(): boolean {
    try {
      const forced = sessionStorage.getItem(FORCE_LOGIN_STORAGE_KEY) === '1';
      if (forced) {
        sessionStorage.removeItem(FORCE_LOGIN_STORAGE_KEY);
      }
      return forced;
    } catch {
      return false;
    }
  }
}
